import {CmChannelType, SosDonationCardType, SosDonationCertificateType, SosDonationFormType, SosDonationProductType, SosDonationToolType} from "../../../types";
import {CustomDonationSettings, Maybe} from "../../../../../packages/graphql-layer/src";
import {
    AdditionalDocTypeParameters,
    DonationContext,
    DonationContextEntries,
    DonationContextProperties,
    DonationProductParameters,
    DonationQueryParams,
    DonationRhythm,
    DonationType,
    FormDataInput,
    FormSettings,
    ProductType,
    ProductTypeSettings,
    SponsorshipType,
    UserSettings
} from "../types";
import {ParsedUrlQuery} from "querystring";
import {TFunction} from "i18next";
import {roundTo, splitToNumberArray, stringToNumber} from "../../../../utility/numberUtil";
import {splitAndTrim} from "../../../../utility/stringUtil";
import {stringToNumberArray} from "../../../../utility/enumUtil";
import {ProductState} from "../../../doctypes/P020DonationProduct/P020DonationProduct";
import {isValidEnumValue} from "./utilFunctions";
import {PageContextType} from "../../../../context/PageContext";

type DonationItemType = SosDonationFormType | SosDonationCardType | CmChannelType | SosDonationToolType;

export const setDonationContextValues = (customDonation: Maybe<Pick<CustomDonationSettings, "donationContext">> | undefined,
                                         item: DonationItemType,
                                         additionalPriorities?: string[]): DonationContextEntries => {
    if (!customDonation || !customDonation.donationContext) {
        const settings = item?.donationSettings?.default?.donation_context || {}
        if (additionalPriorities) {
            Object.entries(DonationContext).forEach(([key]) => {
                const donationContextPriorities = settings[key as DonationContext]["priorities"];
                settings[key as DonationContext]["priorities"] = additionalPriorities.concat(
                    donationContextPriorities.filter(
                        (value: string) => !additionalPriorities.includes(value)));
            });
        }
        return settings;
    }
    const donationContext: DonationContextEntries = {};
    Object.entries(customDonation.donationContext).forEach(([key, value]) => {
        donationContext[key as DonationContext] =
            createContext(value, item?.donationSettings?.default?.donation_context?.[key], item,
                          additionalPriorities);
    });
    return donationContext;
}
export const setDonationContextValuesForProduct = (item: DonationItemType, donationProduct: SosDonationProductType,
                                                   queryParams: ParsedUrlQuery): DonationContextEntries => {
    const productContext = item?.donationSettings?.default?.donation_context?.product || {}
    const productId = queryParams[DonationQueryParams.contentIdForProduct] as string;
    const productArticleNumber = donationProduct?.articleNumber || "CMS-ID: " + productId;
    productContext.priorities = [productArticleNumber];
    return productContext;
}
export const createProductParameters = (item: SosDonationProductType, queryParams: ParsedUrlQuery,
                                        productState?: ProductState): DonationProductParameters => {
    const productId = queryParams[DonationQueryParams.contentIdForProduct] as string;
    const productQuantity = productState?.productQuantity?.toString();
    const productGreeting = productState?.productGreeting;
    const productFirstname = productState?.productFirstname;
    const productSurname = productState?.productSurname;
    const total = calculateProductTotal(item, stringToNumber(productQuantity));
    const productArticleNumber = item?.articleNumber || "CMS-ID: " + productId;
    return {
        productId: productId,
        productGreeting: productGreeting,
        productQuantity: stringToNumber(productQuantity),
        productFirstname: productFirstname,
        productSurname: productSurname,
        productTotal: total,
        productCertificateId: item.donationCertificate && item.donationCertificate.length > 0 ?
            (item.donationCertificate[0] as SosDonationCertificateType).id : undefined,
        productArticleNumber: productArticleNumber
    } as DonationProductParameters
}
export const createAdditionalDocTypeParameters = (item: DonationItemType,
                                                  queryParams: ParsedUrlQuery) => {
    if (item.type == "SOSDonationTool") {
        const sosDonationTool = item as SosDonationToolType;
        return {
            actionId: sosDonationTool.actionId
        } as AdditionalDocTypeParameters
    }
    if (queryParams[DonationQueryParams.contentIdForSettings]) {
        return {
            cmCsId: queryParams[DonationQueryParams.contentIdForSettings] as string
        } as AdditionalDocTypeParameters
    }
}

export const createContext = (customSettings: any, defaultSettings: any,
                              item: DonationItemType,
                              additionalPriorities?: string[] | undefined): DonationContextProperties => {
    const sosDonationForm = extractSosDonationForm(item) as SosDonationFormType;
    const amounts = (customSettings.amounts && splitToNumberArray(customSettings.amounts)) ||
                    defaultSettings?.amounts;

    function getMinAmount() {
        return (customSettings.minAmount && parseInt(customSettings.minAmount)) ||
               defaultSettings?.minAmount;
    }

    function getPriorities() {
        return createPriorities(customSettings.priorities, defaultSettings?.priorities,
                                additionalPriorities);
    }

    return {
        headline: customSettings.headline || (sosDonationForm && sosDonationForm?.subheading1) ||
                  defaultSettings?.headline,
        subheadline: customSettings.subheadline || defaultSettings?.subheadline,
        amounts: amounts,
        description: customSettings.description || defaultSettings?.description,
        giftLabel: customSettings.giftLabel || defaultSettings?.giftLabel,
        minAmount: getMinAmount(),
        priorities: getPriorities(),
        priorityLabel: customSettings.priorityLabel || defaultSettings?.priorityLabel,
        recipientDataIntroText: customSettings.recipientDataIntroText ||
                                defaultSettings?.recipientDataIntroText || "",
        defaultAmount: (customSettings.defaultAmount && parseInt(customSettings.defaultAmount)) ||
                       defaultSettings?.defaultAmount ||
                       (amounts && amounts.length > 1 ? amounts[1] :
                           (amounts ? amounts[0] : undefined)),
        pdfCertificate: defaultSettings?.pdfCertificate || null,
        buttonLabel: customSettings.buttonLabel || defaultSettings?.buttonLabel,
        isSingleAmountForm: amounts && amounts.length === 1 && (amounts[0] === getMinAmount()),

    };
}

export const extractSosDonationForm = (item: DonationItemType) => {
    if (item.type == "SOSDonationForm") {
        return item as SosDonationFormType;
    }
    if (item.type == "SOSDonationTool") {
        const sosDonationTool = item as SosDonationToolType;
        return sosDonationTool!.donationForm![0] as SosDonationFormType;
    }
    return null;
}

export const createFormSettings = (item: DonationItemType,
                                   t: TFunction): FormSettings | null => {
    const sosDonationForm = extractSosDonationForm(item) as SosDonationFormType;

    if (sosDonationForm == null) {
        return null;
    }
    return {
        formHeadline: sosDonationForm?.subheading1 || t("donation_form.headline"),
        paymentHeadline: sosDonationForm.subheading2 || t("donation_form.accountSubline"),
        personalDataHeadline: sosDonationForm.subheading3 || t("donation_form.addressSubline"),
        optionalLabel: sosDonationForm?.optionalLabel || t("address_form.optional_information"),
        newsletterAgree: sosDonationForm.newsletterAgree || "",
        privacyInfo: sosDonationForm.privacyInfo || "",
        submit_hint: sosDonationForm.donationSettings?.default?.submit_hint,
        payment_hint: sosDonationForm.donationSettings?.default?.payment_hint,
        payment_method: sosDonationForm.donationSettings?.default?.payment_method,
        agreementText: sosDonationForm.donationSettings?.default?.agreementText,
        privacyUrl: sosDonationForm.donationSettings?.default?.privacyUrl,
        saferPayAccount: sosDonationForm.saferPayAccount || "",
    }
}

export const createFormInput = (item: DonationItemType, queryParams: ParsedUrlQuery, t: TFunction,
                                additionalPriorities?: string[],
                                donationProduct?: SosDonationProductType): FormDataInput => {
    const isProduct = donationProduct != null;
    const customSettings: Maybe<CustomDonationSettings> | undefined = item.donationSettings?.custom;
    return {
        productType: isProduct ? createProductTypeForProduct() :
            customSettings?.productType &&
            createProductType(customSettings.productType) || null,
        donationContext: isProduct ?
            setDonationContextValuesForProduct(item, donationProduct, queryParams) :
            setDonationContextValues(customSettings, item, additionalPriorities),
        formSettings: createFormSettings(item, t),
        userSettings: createUserSettings(queryParams),
        additionalDocTypeParameters: createAdditionalDocTypeParameters(item, queryParams),
        // Same section-visibility defaults as the contentId branch in CCODonationInitializer -
        // without them the flags stay undefined and the optional, gift and newsletter sections are
        // hidden on every form that is rendered straight from its placement item.
        onlyNameAndEmailForm: customSettings?.onlyNameAndEmailForm ?? false,
        showOptionalInformation: customSettings?.showOptionalInformation ?? true,
        showGiftDonation: customSettings?.showGiftDonation ?? true,
        showNewsletterRegistration: customSettings?.showNewsletterRegistration ?? true,
    }
}

export const createProductType = (productTypeSettings: Maybe<ProductTypeSettings> | undefined): ProductType => {
    return {
        donationType: productTypeSettings ? stringToNumberArray(productTypeSettings.donationType) :
            [DonationType.Donation],
        donationRhythm: productTypeSettings ?
            stringToNumberArray(productTypeSettings.donationRhythm) : [DonationRhythm.Unique],
        sponsorshipType: productTypeSettings ?
            stringToNumberArray(productTypeSettings.sponsorshipType) : undefined,
    }
}

export const createProductTypeForProduct = () => {
    return {
        donationType: [DonationType.Product],
        donationRhythm: [DonationRhythm.Unique],
        sponsorshipType: undefined,
    }
}

const parseEnumQueryParam = <T extends number>(value: ParsedUrlQuery[string],
                                               enumObject: Record<string, T | string>): T | undefined =>
    typeof value === "string" && /^\d+$/.test(value) && isValidEnumValue(Number(value), enumObject)
        ? Number(value) as T
        : undefined;

export const createUserSettings = (queryParams: ParsedUrlQuery) => {
    const userSettings: UserSettings = {}

    if (queryParams[DonationQueryParams.amount]) {
        userSettings.amount = parseFloat(queryParams[DonationQueryParams.amount] as string);
    }
    const donationType = parseEnumQueryParam(queryParams[DonationQueryParams.donationType], DonationType);
    if (donationType !== undefined) {
        userSettings.donationType = donationType;
    }
    const donationRhythm = parseEnumQueryParam(queryParams[DonationQueryParams.donationRhythm], DonationRhythm);
    if (donationRhythm !== undefined) {
        userSettings.donationRhythm = donationRhythm;
    }
    const sponsorshipType = parseEnumQueryParam(queryParams[DonationQueryParams.sponsorshipType], SponsorshipType);
    if (sponsorshipType !== undefined) {
        userSettings.sponsorshipType = sponsorshipType;
    }
    if (queryParams[DonationQueryParams.priority]) {
        userSettings.priority = decodeURIComponent(queryParams[DonationQueryParams.priority] as string);
    }
    if (queryParams[DonationQueryParams.gift]) {
        userSettings.gift = decodeURIComponent(queryParams[DonationQueryParams.gift] as string);
    }
    if (queryParams[DonationQueryParams.contentIdForProduct]) {
        userSettings.sponsorshipType = undefined;
    }
    return userSettings;
}


export const createPriorities = (
    customPriorities: string | undefined,
    defaultPriorities: string[] | undefined,
    additionalPriorities: string[] = []
) => {
    additionalPriorities = Array.isArray(additionalPriorities) ? additionalPriorities : [];
    const settingsPriorities = customPriorities
        ? splitAndTrim(customPriorities)
        : (Array.isArray(defaultPriorities) ? defaultPriorities : []);

    return additionalPriorities.concat(
        settingsPriorities.filter((value: string) => !additionalPriorities.includes(value))
    );
};

export function getAdditionalPriorities(pageContext: PageContextType, queryParams: ParsedUrlQuery) {
    if (queryParams[DonationQueryParams.priority]) {
        return [decodeURIComponent(queryParams[DonationQueryParams.priority] as string)];
    }
    if (pageContext.taxonomies?.length > 0) {
        return additionalPriorities(pageContext);
    }
}

const additionalPriorities = (pageContext: PageContextType) => {
    const prios: string[] = [];
    const taxonomies = pageContext.taxonomies;
    if (taxonomies) {
        taxonomies.map((name: string) => (
            prios.push(name)));
    }
    return prios;
}

function calculateProductTotal(item: SosDonationProductType, productQuantity: number) {
    const price = stringToNumber(item.price || "0");
    return roundTo(productQuantity * price, 2);
}